import {divideRatio} from "./reportingMetricRatios.js";
import {mediaSourceQualityBlocksMetrics, normalizeMediaSourceQuality} from "./reportingSnapshotAvailability.js";
export {mediaSourceQualityBlocksMetrics, mediaSourceQualityHasValidatedCurrentDayPartial, normalizeMediaSourceQuality} from "./reportingSnapshotAvailability.js";

export const CampaignSegment = {
    AboveBreakEven: "aboveBreakEven",
    Loss: "loss",
    LowData: "lowData",
};

/** Aggregate only known quality objects, preserving a missing field as legacy/unknown scope. */
export function combineMediaSourceQualities(qualities) {
    const normalized = (Array.isArray(qualities) ? qualities : [qualities])
        .map(normalizeMediaSourceQuality)
        .filter(Boolean);
    if (normalized.length === 0) return null;

    const applicable = normalized.filter((quality) => quality.applicable);
    if (applicable.length === 0) {
        return {applicable: false, available: true, complete: true, issues: []};
    }

    const issueKeys = new Set();
    const issues = [];
    for (const quality of applicable) {
        for (const issue of quality.issues) {
            const key = [issue.reason, issue.integrationId ?? "", issue.accountId ?? "", issue.campaignId ?? "", issue.premiseId ?? "", issue.day ?? ""].join("|");
            if (!issueKeys.has(key)) {
                issueKeys.add(key);
                issues.push(issue);
            }
        }
    }

    return {
        version: applicable.every((quality) => quality.version === 1) ? 1 : undefined,
        applicable: true,
        available: applicable.some((quality) => quality.available),
        complete: applicable.every((quality) => quality.complete),
        issues,
        currentDayPartialScopeExplained: applicable.every((quality) => quality.currentDayPartialScopeExplained === true),
    };
}

export function mediaSourceQualityReasonCopy(t, reason, locale = "cs") {
    const languageIndex = String(locale).toLowerCase().startsWith("en") ? 1 : 0;
    switch (reason) {
        case "classification_unknown":
            return t("reporting_lab.media_source_quality.classification_unknown", {defaultValue: languageIndex === 1 ? "Part of the advertising costs could not be classified." : "Nepodařilo se potvrdit zařazení části reklamních nákladů."});
        case "fenix_coverage_missing":
            return t("reporting_lab.media_source_quality.fenix_coverage_missing", {defaultValue: languageIndex === 1 ? "Completeness of the advertising source data cannot be confirmed." : "Úplnost dat tohoto reklamního zdroje zatím nelze potvrdit."});
        case "fenix_coverage_partial":
            return t("reporting_lab.media_source_quality.fenix_coverage_partial", {defaultValue: languageIndex === 1 ? "Some data from the advertising source is missing for this period." : "Za toto období chybí část dat z reklamního zdroje."});
        case "fenix_unavailable":
            return t("reporting_lab.media_source_quality.fenix_unavailable", {defaultValue: languageIndex === 1 ? "Data from the advertising source is unavailable for this period." : "Data z reklamního zdroje za toto období nejsou dostupná."});
        case "source_identity_ambiguous":
            return t("reporting_lab.media_source_quality.source_identity_ambiguous", {defaultValue: languageIndex === 1 ? "The advertising source could not be identified reliably." : "Nepodařilo se jednoznačně určit reklamní zdroj."});
        case "fenix_generation_mismatch":
            return t("reporting_lab.media_source_quality.fenix_generation_mismatch", {defaultValue: languageIndex === 1 ? "Completeness of the advertising source data cannot be confirmed." : "Úplnost dat tohoto reklamního zdroje zatím nelze potvrdit."});
        case "media_source_quality_unknown":
        default:
            return t("reporting_lab.media_source_quality.media_source_quality_unknown", {defaultValue: languageIndex === 1 ? "Completeness of the advertising source data cannot be confirmed." : "Úplnost dat tohoto reklamního zdroje zatím nelze potvrdit."});
    }
}

export function mediaFinanceScopeLabel(financeScope, t, locale = "cs") {
    const english = String(locale).toLowerCase().startsWith("en");
    if (financeScope === "raw_provider") {
        return t("reporting_lab.media_source_quality.raw_detail", {
            defaultValue: english
                ? "Raw provider data; it may be excluded from selected advertising costs."
                : "Původní data poskytovatele; nemusí vstupovat do zvolených reklamních nákladů.",
        });
    }
    if (financeScope === "canonical_selected") {
        return t("reporting_lab.media_source_quality.canonical_selected", {
            defaultValue: english ? "Selected advertising costs" : "Zvolené reklamní náklady",
        });
    }
    return null;
}

export function classifyCampaignSegment({spend, turnover, profit, orders, hasProfit, hasPoas, poas}) {
    let segment = CampaignSegment.LowData;

    if (!hasProfit || !hasPoas) {
        return CampaignSegment.LowData;
    }

    if (spend > 0 && profit < 0) {
        segment = CampaignSegment.Loss;
    } else if (spend > 0 && orders < 3) {
        segment = CampaignSegment.LowData;
    } else if (spend > 0 && poas !== null && poas >= 1) {
        segment = CampaignSegment.AboveBreakEven;
    } else if (spend > 0) {
        segment = CampaignSegment.Loss;
    }

    return segment;
}

export function averageOrderValueForPeriod(turnover, orderCount) {
    return orderCount > 0 ? turnover / orderCount : null;
}

export function reportedCentreAverageOrderValue(centre) {
    const orderCount = Number(centre?.orderAmount);
    const value = centre?.averageOrderPrice;
    const amount = value !== null && typeof value === "object" ? value.amount : value;

    if (
        centre?.orderAmount === null || centre?.orderAmount === undefined || centre?.orderAmount === ""
        || !Number.isFinite(orderCount) || orderCount <= 0
        || value === null || value === undefined || amount === null || amount === undefined || amount === ""
        || !Number.isFinite(Number(amount))
    ) {
        return null;
    }

    return value;
}

function moneyAmount(value) {
    return value !== null && typeof value === "object" ? value.amount : value;
}

function finiteMoneyAmount(value) {
    const amount = moneyAmount(value);
    if (amount === null || amount === undefined || amount === "") return null;
    const numericAmount = Number(amount);
    return Number.isFinite(numericAmount) ? numericAmount : null;
}

export function calculateMarketingPoas(rows, mediaSourceQuality = null) {
    if (mediaSourceQualityBlocksMetrics(mediaSourceQuality)) return null;
    if (!rows.length || !rows.every((row) => row.purchaseCostQualityAvailable === true
        && row.purchaseCostComplete === true
        && !mediaSourceQualityBlocksMetrics(row.mediaSourceQuality)
        && finiteMoneyAmount(row.turnover) !== null
        && finiteMoneyAmount(row.contributionBeforeMarketing) !== null
        && finiteMoneyAmount(row.spendMarketing) !== null)) {
        return null;
    }

    const turnover = rows.reduce((total, row) => total + finiteMoneyAmount(row.turnover), 0);
    if (turnover < 0) return null;

    const contributionBeforeMarketing = rows.reduce((total, row) => total + finiteMoneyAmount(row.contributionBeforeMarketing), 0);
    const marketingSpend = rows.reduce((total, row) => total + finiteMoneyAmount(row.spendMarketing), 0);

    return divideRatio(contributionBeforeMarketing, marketingSpend);
}

export function calculateMarketingRoas(turnover, spend, mediaSourceQuality = null) {
    const revenue = finiteMoneyAmount(turnover);
    const marketingSpend = finiteMoneyAmount(spend);
    if (revenue === null || marketingSpend === null || revenue < 0) return null;
    return divideRatio(revenue, marketingSpend, mediaSourceQuality);
}

export function calculateCompleteCampaignProfit(rows, basis = "gross") {
    const suffix = basis === "completed" ? "Net" : "Gross";

    if (!rows.length || !rows.every((row) => row.purchaseCostQualityAvailable === true
        && row[`purchaseCostComplete${suffix}`] === true
        && row[`purchaseCostUnknown${suffix}`] === 0
        && row.profit?.amount != null
        && Number.isFinite(Number(row.profit.amount)))) {
        return null;
    }

    return rows.reduce((total, row) => total + Number(row.profit.amount), 0);
}

export function rankCompleteAboveBreakEvenCampaigns(campaigns, limit = 6, basis = "gross") {
    const suffix = basis === "completed" ? "Net" : "Gross";

    return campaigns
        .filter((campaign) => campaign.segment === CampaignSegment.AboveBreakEven
            && campaign.purchaseCostQualityAvailable === true
            && campaign[`purchaseCostComplete${suffix}`] === true
            && campaign[`purchaseCostUnknown${suffix}`] === 0
            && campaign.profit?.amount != null
            && Number.isFinite(Number(campaign.profit.amount))
            && campaign.poas != null
            && Number.isFinite(Number(campaign.poas))
            && Number(campaign.poas) >= 1
            && Number(campaign.profit.amount) >= 0)
        .sort((left, right) => Number(right.profit.amount) - Number(left.profit.amount))
        .slice(0, limit);
}

export function campaignPurchaseCostQuality(campaign, basis = "gross") {
    const suffix = basis === "completed" ? "Net" : "Gross";

    return {
        purchaseCostQualityAvailable: campaign.purchaseCostQualityAvailable,
        [`purchaseCostUnknown${suffix}`]: campaign[`purchaseCostUnknown${suffix}`],
        [`purchaseCostEstimated${suffix}`]: campaign[`purchaseCostEstimated${suffix}`],
        [`purchaseCostComplete${suffix}`]: campaign[`purchaseCostComplete${suffix}`],
    };
}

export function calculateCompletePeriodPoas(rows, basis = "gross") {
    const fields = basis === "completed"
        ? {revenue: "turnoverCompleted", margin: "goodsMarginCompleted", complete: "purchaseCostCompleteCompleted", unknown: "purchaseCostUnknownCompleted"}
        : {revenue: "turnover", margin: "goodsMargin", complete: "purchaseCostCompleteGross", unknown: "purchaseCostUnknownGross"};

    if (!rows.length || !rows.every((row) => row.purchaseCostQualityAvailable !== false
        && !mediaSourceQualityBlocksMetrics(row.mediaSourceQuality)
        && row[fields.complete] === true
        && row[fields.unknown] === 0
        && finiteMoneyAmount(row[fields.revenue]) !== null
        && finiteMoneyAmount(row[fields.margin]) !== null
        && finiteMoneyAmount(row.spend) !== null)) {
        return null;
    }

    const goodsMargin = rows.reduce((total, row) => total + finiteMoneyAmount(row[fields.margin]), 0);
    const spend = rows.reduce((total, row) => total + finiteMoneyAmount(row.spend), 0);
    const revenue = rows.reduce((total, row) => total + finiteMoneyAmount(row[fields.revenue]), 0);
    if (revenue < 0) return null;

    return divideRatio(goodsMargin, spend);
}

export function poasUnavailableReasons(rows, fields, isUnavailable) {
    if (!Array.isArray(rows) || rows.length === 0) {
        return isUnavailable ? ["unavailable"] : [];
    }

    const reasons = [];
    const hasExplicitCostGap = rows.some((row) => row[fields.costComplete] === false
        || (typeof row[fields.costUnknown] === "number" && row[fields.costUnknown] > 0));

    if (hasExplicitCostGap) reasons.push("incompleteCosts");
    else if (!isUnavailable && rows.some((row) => Number(row[fields.costEstimated]) > 0)) reasons.push("estimatedCosts");

    const revenueAmounts = rows.map((row) => finiteMoneyAmount(row[fields.revenue]));
    if (revenueAmounts.every((amount) => amount !== null)
        && revenueAmounts.reduce((sum, amount) => sum + amount, 0) < 0) {
        reasons.push("negativeRevenue");
    }

    const spendAmounts = rows.map((row) => finiteMoneyAmount(row[fields.spend]));
    if (spendAmounts.every((amount) => amount !== null)
        && spendAmounts.reduce((sum, amount) => sum + amount, 0) === 0) {
        reasons.push("zeroSpend");
    }

    if (isUnavailable && reasons.length === 0) reasons.push("unavailable");
    return reasons;
}

export function poasUnavailableCopy(reasons, t) {
    const copy = {
        incompleteCosts: {
            short: t("reporting_lab.poas_unavailable.incomplete_short", {defaultValue: "Neúplné náklady"}),
            detail: t("reporting_lab.poas_unavailable.incomplete_detail", {defaultValue: "Část nákupních nákladů dokončených objednávek chybí nebo nemá ověřený původ."}),
        },
        estimatedCosts: {
            short: t("reporting_lab.poas_unavailable.estimated_short", {defaultValue: "Odhad"}),
            detail: t("reporting_lab.poas_unavailable.estimated_detail", {defaultValue: "POAS zahrnuje odhadované nákupní ceny. Jde o odhad výsledku, nikoli o doložené historické ocenění."}),
        },
        zeroSpend: {
            short: t("reporting_lab.poas_unavailable.zero_spend_short", {defaultValue: "Bez nákladů na reklamu"}),
            detail: t("reporting_lab.poas_unavailable.zero_spend_detail", {defaultValue: "Při nulových nákladech na reklamu není POAS definován."}),
        },
        negativeRevenue: {
            short: t("reporting_lab.poas_unavailable.negative_revenue_short", {defaultValue: "Záporný obrat"}),
            detail: t("reporting_lab.poas_unavailable.negative_revenue_detail", {defaultValue: "Souhrnný obrat dokončených objednávek je záporný, proto POAS pro toto období není dostupný."}),
        },
        unavailable: {
            short: t("reporting_lab.poas_unavailable.unavailable_short", {defaultValue: "Nedostupné"}),
            detail: t("reporting_lab.poas_unavailable.unavailable_detail", {defaultValue: "POAS nelze pro toto období spolehlivě vyčíslit z dostupných údajů."}),
        },
    };
    const entries = reasons.map((reason) => copy[reason]).filter(Boolean);

    return {
        short: entries.map((entry) => entry.short).join(" · "),
        detail: entries.map((entry) => entry.detail).join(" "),
    };
}
